"use client";

import { Plus, Minus, Trash2, Truck } from "lucide-react";
import { CartItem } from "@/lib/store/cartStore";
import { Id } from "@/convex/_generated/dataModel";
import { ColorSwatch } from "@/components/ui/ColorSwatch";
import type { BagLineSummary } from "@/lib/config/websiteAddOns";

interface CartItemCardProps {
  item: CartItem;
  availableStock: number;
  isLoadingStock: boolean;
  onQuantityUpdate: (cartItemId: Id<"cartItems">, newQuantity: number) => void;
  onRemove: (cartItemId: Id<"cartItems">) => void;
  /** What the item comes with, the extras ticked and its free delivery (lib/config/websiteAddOns.ts bagSummary). */
  addOns?: BagLineSummary;
}

export function CartItemCard({
  item,
  availableStock,
  isLoadingStock,
  onQuantityUpdate,
  onRemove,
  addOns,
}: CartItemCardProps) {
  const isAtMaxStock = item.quantity >= availableStock;
  const isLowStock = availableStock <= 5 && availableStock > 0;
  // `price` is what the line is charged (the checkout sends it); `salePrice`
  // holds the product's old price before a sale, shown crossed out.
  const oldPrice = Number(item.salePrice) > Number(item.price) ? Number(item.salePrice) : 0;

  return (
    // Compact layout — tighter padding, smaller image, and the price +
    // quantity controls sit on a single row so a long cart shows more
    // items per screen without scrolling.
    <div className="flex gap-3 p-2.5 bg-gray-50 rounded-lg">
      {/* Product Image — plain <img> with a fallback so a missing/broken or
          unconfigured-domain URL never shows a broken-image icon. */}
      <div className="relative w-14 h-14 rounded-md overflow-hidden shrink-0 bg-white">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={item.productImage || "/images/placeholder-product.jpg"}
          alt={item.productName}
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
          onError={(e) => {
            const el = e.currentTarget as HTMLImageElement;
            if (!el.src.endsWith("/images/placeholder-product.jpg")) {
              el.src = "/images/placeholder-product.jpg";
            }
          }}
        />
      </div>

      {/* Product Details */}
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-black text-sm leading-tight line-clamp-1">
          {item.productName}
        </h3>

        {/* Variant Details — color + size on one compact row */}
        {(item.selectedVariants?.color ||
          item.selectedVariants?.isMulticolor ||
          item.selectedVariants?.size) && (
          <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
            {(item.selectedVariants?.color || item.selectedVariants?.isMulticolor) && (
              <span className="flex items-center gap-1">
                <ColorSwatch
                  hex={item.selectedVariants.colorHex || item.selectedVariants.color || ""}
                  isMulticolor={item.selectedVariants.isMulticolor}
                  multicolorList={item.selectedVariants.multicolorList}
                  size="sm"
                />
                {item.selectedVariants.color && item.selectedVariants.color !== "Multicolor" && (
                  <span className="text-xs text-gray-600">{item.selectedVariants.color}</span>
                )}
              </span>
            )}
            {item.selectedVariants?.size && (
              <span className="text-xs text-gray-500">Size: {item.selectedVariants.size}</span>
            )}
          </div>
        )}

        {/* What it comes with, and the extras ticked (Oct 2026). */}
        {addOns && addOns.free.length > 0 && (
          <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">
            Comes with {addOns.free.map((p) => `${p.qty * item.quantity} × ${p.name}`).join(", ")}
          </p>
        )}
        {addOns && addOns.notIncluded.length > 0 && (
          <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-2">
            Not included (out of stock): {addOns.notIncluded.map((p) => `${p.qty * item.quantity} × ${p.name}`).join(", ")}
          </p>
        )}
        {addOns && addOns.paid.length > 0 && (
          <ul className="mt-0.5 space-y-0.5">
            {addOns.paid.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-2 text-[11px] text-gray-700">
                <span className="truncate">
                  + {p.qty * item.quantity} × {p.name}
                </span>
                <span className="shrink-0 tabular-nums">{(p.price * p.qty * item.quantity).toLocaleString()} UGX</span>
              </li>
            ))}
          </ul>
        )}
        {addOns?.freeDelivery && (
          <span className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700">
            <Truck className="w-3 h-3" aria-hidden /> Free delivery
          </span>
        )}

        {/* Stock Indicator */}
        {!isLoadingStock && isLowStock && (
          <p className="text-xs text-orange-600 mt-0.5">
            Only {availableStock} left
          </p>
        )}

        {/* Price + Quantity controls on one row to save vertical space */}
        <div className="flex items-center justify-between mt-1.5 gap-2">
          <p className="text-sm font-bold text-black whitespace-nowrap">
            {Math.floor(item.price).toLocaleString()} UGX
            {oldPrice > 0 && (
              <span className="ml-1 text-[11px] font-normal text-gray-400 line-through">
                {Math.floor(oldPrice).toLocaleString()}
              </span>
            )}
          </p>

          <div className="flex items-center gap-1">
            <div className="flex items-center border border-gray-300 rounded-md">
              <button
                onClick={() => onQuantityUpdate(item._id, item.quantity - 1)}
                className="p-1 hover:bg-gray-100 transition-colors disabled:opacity-50"
                aria-label="Decrease quantity"
              >
                <Minus className="w-3.5 h-3.5 text-black" />
              </button>
              <span className="px-2 text-sm font-semibold text-black min-w-[24px] text-center">
                {item.quantity}
              </span>
              <button
                onClick={() => onQuantityUpdate(item._id, item.quantity + 1)}
                disabled={isAtMaxStock}
                className="p-1 hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title={isAtMaxStock ? "Maximum stock reached" : "Increase quantity"}
                aria-label="Increase quantity"
              >
                <Plus className="w-3.5 h-3.5 text-black" />
              </button>
            </div>

            <button
              onClick={() => onRemove(item._id)}
              className="p-1.5 hover:bg-red-50 rounded-md transition-colors"
              aria-label="Remove item"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
